var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tooltip } from '@web/components/ui/Tooltip';
import { classNames } from '@web/utility';
var MINUTES_PER_HOUR = 60;
var HOURS_ON_CLOCK = 24;
var DISPLAY_TIME_LENGTH = 5;
var PERCENT_SCALE = 100;
var MIN_DOMAIN_SPAN_MINUTES = 1;
var MIN_AXIS_TICK_GAP_FROM_BOUNDARY_MINUTES = 25;
var AXIS_TICK_STEP_CANDIDATES_MINUTES = [30, 60, 120, 180, 240, 360];
var AXIS_DESIRED_INTERIOR_TICK_COUNT = 5;
var AXIS_LABEL_LEFT_ALIGN_MAX_PERCENT = 0.75;
var AXIS_LABEL_RIGHT_ALIGN_MIN_PERCENT = 99.25;
var MIN_ZERO_DURATION_BAR_WIDTH_PERCENT = 0.5;
export function timeToMinutes(timeString) {
    var _a = timeString.split(':').map(Number), hours = _a[0], minutes = _a[1], _b = _a[2], seconds = _b === void 0 ? 0 : _b;
    return hours * MINUTES_PER_HOUR + minutes + seconds / MINUTES_PER_HOUR;
}
function buildBarTimelineLayout(items, timelineStart, timelineEnd) {
    var itemsWithMinutes = items.map(function (item) { return (__assign(__assign({}, item), { startMinutes: timeToMinutes(item.startTime), endMinutes: timeToMinutes(item.endTime) })); });
    var domainStartMinutes = timeToMinutes(timelineStart);
    var domainEndMinutes = timeToMinutes(timelineEnd);
    for (var _i = 0, itemsWithMinutes_1 = itemsWithMinutes; _i < itemsWithMinutes_1.length; _i++) {
        var item = itemsWithMinutes_1[_i];
        if (item.startMinutes < domainStartMinutes) {
            domainStartMinutes = item.startMinutes;
        }
        if (item.endMinutes > domainEndMinutes) {
            domainEndMinutes = item.endMinutes;
        }
    }
    return {
        itemsWithMinutes: itemsWithMinutes,
        domainStartMinutes: domainStartMinutes,
        totalMinutes: Math.max(domainEndMinutes - domainStartMinutes, MIN_DOMAIN_SPAN_MINUTES)
    };
}
export function formatTimeRange(startTime, endTime) {
    var formatTime = function (time) { return time.substring(0, DISPLAY_TIME_LENGTH); };
    return "".concat(formatTime(startTime), " - ").concat(formatTime(endTime));
}
export function formatTooltip(item) {
    var timeRange = formatTimeRange(item.startTime, item.endTime);
    if (!item.title) {
        return timeRange;
    }
    if (typeof item.title !== 'string') {
        return item.title;
    }
    return "".concat(item.title, ": ").concat(timeRange);
}
function formatAxisLabelFromMinutes(totalMinutes) {
    var h = Math.floor(totalMinutes / MINUTES_PER_HOUR) % HOURS_ON_CLOCK;
    var m = Math.floor(totalMinutes % MINUTES_PER_HOUR);
    return "".concat(String(h).padStart(2, '0'), ":").concat(String(m).padStart(2, '0'));
}
function filterAxisTicksNearBoundaries(sortedMinutes, domainStartMinute, domainEndMinute) {
    return sortedMinutes.filter(function (m) {
        if (m === domainStartMinute || m === domainEndMinute)
            return true;
        var distToStart = m - domainStartMinute;
        var distToEnd = domainEndMinute - m;
        return (distToStart >= MIN_AXIS_TICK_GAP_FROM_BOUNDARY_MINUTES &&
            distToEnd >= MIN_AXIS_TICK_GAP_FROM_BOUNDARY_MINUTES);
    });
}
export function getTimelineAxisTicks(timelineStart, timelineEnd) {
    var _a;
    var domainStartMinute = timeToMinutes(timelineStart);
    var domainEndMinute = timeToMinutes(timelineEnd);
    var totalM = Math.max(domainEndMinute - domainStartMinute, MIN_DOMAIN_SPAN_MINUTES);
    var divisor = Math.max(AXIS_DESIRED_INTERIOR_TICK_COUNT - 1, 1);
    var rawStep = totalM / divisor;
    var stepM = (_a = AXIS_TICK_STEP_CANDIDATES_MINUTES.find(function (s) { return s >= rawStep; })) !== null && _a !== void 0 ? _a : AXIS_TICK_STEP_CANDIDATES_MINUTES[AXIS_TICK_STEP_CANDIDATES_MINUTES.length - 1];
    var ticks = new Set([domainStartMinute, domainEndMinute]);
    var t = Math.ceil(domainStartMinute / stepM) * stepM;
    while (t <= domainEndMinute) {
        ticks.add(t);
        t += stepM;
    }
    var sorted = Array.from(ticks).sort(function (a, b) { return a - b; });
    var filtered = filterAxisTicksNearBoundaries(sorted, domainStartMinute, domainEndMinute);
    return filtered.map(function (m) { return ({
        minute: m,
        label: formatAxisLabelFromMinutes(m),
        offsetPercent: ((m - domainStartMinute) * PERCENT_SCALE) / totalM
    }); });
}
export function TimelineTimeAxis(_a) {
    var timelineStart = _a.timelineStart, timelineEnd = _a.timelineEnd, className = _a.className, children = _a.children;
    var ticks = getTimelineAxisTicks(timelineStart, timelineEnd);
    return (_jsx("div", __assign({ className: classNames('relative w-full', className) }, (!children ? { 'aria-hidden': true } : {}), { children: _jsxs("div", __assign({ className: "relative h-4 w-full" }, { children: [children, ticks.map(function (_a) {
                    var minute = _a.minute, label = _a.label, offsetPercent = _a.offsetPercent;
                    var isStart = offsetPercent <= AXIS_LABEL_LEFT_ALIGN_MAX_PERCENT;
                    var isEnd = offsetPercent >= AXIS_LABEL_RIGHT_ALIGN_MIN_PERCENT;
                    return (_jsx("span", __assign({ "aria-hidden": true, className: "absolute top-0 text-xs tabular-nums text-[#c7c9cf]", style: {
                            left: "".concat(offsetPercent, "%"),
                            transform: isStart
                                ? 'translateX(0)'
                                : isEnd
                                    ? 'translateX(-100%)'
                                    : 'translateX(-50%)'
                        } }, { children: label }), minute));
                })] })) })));
}
export var SimpleBarTimeline = function (_a) {
    var items = _a.items, barColor = _a.barColor, timelineStart = _a.timelineStart, timelineEnd = _a.timelineEnd;
    var _b = buildBarTimelineLayout(items, timelineStart, timelineEnd), itemsWithMinutes = _b.itemsWithMinutes, domainStartMinutes = _b.domainStartMinutes, totalMinutes = _b.totalMinutes;
    return (_jsx("div", __assign({ className: "py-2" }, { children: _jsx("div", __assign({ className: "relative h-6" }, { children: _jsx("div", __assign({ className: "absolute left-0 top-1/2 h-8 w-full -mt-4 rounded-md bg-gray-200" }, { children: itemsWithMinutes.map(function (item) {
                    var offsetMinutes = item.startMinutes - domainStartMinutes;
                    var durationMinutes = item.endMinutes - item.startMinutes;
                    var leftPercent = (offsetMinutes * PERCENT_SCALE) / totalMinutes;
                    var rawWidthPercent = (durationMinutes * PERCENT_SCALE) / totalMinutes;
                    var widthPercent = durationMinutes === 0
                        ? Math.max(rawWidthPercent, MIN_ZERO_DURATION_BAR_WIDTH_PERCENT)
                        : rawWidthPercent;
                    return (_jsx("div", __assign({ className: "absolute", style: {
                            left: "".concat(leftPercent, "%"),
                            width: "".concat(widthPercent, "%")
                        } }, { children: _jsx(Tooltip, __assign({ content: formatTooltip(item), className: "!transition-none" }, { children: _jsx("div", { className: "h-8 ".concat(barColor, " rounded-md shadow").concat(item.onBarClick ? ' cursor-pointer' : ''), role: item.onBarClick ? 'button' : undefined, tabIndex: item.onBarClick ? 0 : undefined, onClick: function (e) {
                                    var _a;
                                    e.stopPropagation();
                                    (_a = item.onBarClick) === null || _a === void 0 ? void 0 : _a.call(item);
                                }, onKeyDown: function (e) {
                                    if (!item.onBarClick)
                                        return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        item.onBarClick();
                                    }
                                } }) })) }), item.id));
                }) })) })) })));
};
